import { passwordChangeSchema, validate } from "@love-cpmd/core";
import { Button, Field, Input, toast } from "@love-cpmd/ui";
import { Check, Eye, EyeOff, KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiError, changePassword } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { LogoMark } from "../common/layout-bits";

function PasswordInput({ id, value, onChange, invalid, autoComplete, autoFocus }: { id: string; value: string; onChange: (v: string) => void; invalid?: boolean; autoComplete: string; autoFocus?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <Input
      id={id}
      type={show ? "text" : "password"}
      value={value}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      invalid={invalid}
      leading={<KeyRound />}
      trailing={
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"} className="inline-flex size-9 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container">
          {show ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
        </button>
      }
    />
  );
}

const RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: "At least 8 characters", test: (v) => v.length >= 8 },
  { label: "An uppercase and a lowercase letter", test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { label: "A number", test: (v) => /[0-9]/.test(v) },
];

/**
 * First sign-in (or after a reset by the System Manager): the default or temporary password
 * has to be replaced before the app opens. The server enforces the same rule.
 */
export function FirstPasswordScreen() {
  const { userId, logout, refresh } = useAuth();
  const [values, setValues] = useState({ current: "", next: "", confirm: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const check = validate(passwordChangeSchema, values);
    if (!check.ok) return setErrors(check.errors);
    setErrors({});
    setPending(true);
    try {
      await changePassword(userId ?? "", values.current, values.next);
      await refresh();
      toast.success("Password saved. Welcome to LOVE CPMD!");
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else setErrors({ _form: err instanceof Error ? err.message : "Something went wrong. Please try again." });
    } finally {
      setPending(false);
    }
  };

  const set = (key: keyof typeof values) => (v: string) => setValues((s) => ({ ...s, [key]: v }));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-10">
      <div className="relative w-full max-w-[460px] overflow-hidden rounded-[22px] border border-white/70 bg-surface-container-lowest p-6 shadow-elevated sm:p-8">
        <div aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-secondary to-primary-container" />
        <div className="flex flex-col items-center pb-6 text-center">
          <LogoMark className="mb-3 size-16" decorative />
          <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-3 py-1 text-label-sm font-semibold text-primary">
            <ShieldCheck className="size-4" aria-hidden /> One more step
          </span>
          <h1 className="text-headline-md text-on-surface">Choose your own password</h1>
          <p className="mt-1 max-w-sm text-body-md text-on-surface-variant">
            You signed in with a default or temporary password. Replace it with one only you know to continue.
          </p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <input type="text" autoComplete="username" className="hidden" aria-hidden tabIndex={-1} readOnly />
          <Field label="Password you just used" htmlFor="fp-current" error={errors.current}>
            <PasswordInput id="fp-current" autoComplete="current-password" value={values.current} onChange={set("current")} invalid={!!errors.current} autoFocus />
          </Field>
          <Field label="New password" htmlFor="fp-next" error={errors.next}>
            <PasswordInput id="fp-next" autoComplete="new-password" value={values.next} onChange={set("next")} invalid={!!errors.next} />
          </Field>
          <ul className="-mt-1 flex flex-col gap-1 text-body-sm" aria-label="Password rules">
            {RULES.map((r) => {
              const ok = r.test(values.next);
              return (
                <li key={r.label} className={`flex items-center gap-2 ${ok ? "text-completed" : "text-on-surface-variant"}`}>
                  <Check className={`size-4 ${ok ? "opacity-100" : "opacity-30"}`} aria-hidden />
                  {r.label}
                </li>
              );
            })}
          </ul>
          <Field label="Confirm new password" htmlFor="fp-confirm" error={errors.confirm}>
            <PasswordInput id="fp-confirm" autoComplete="new-password" value={values.confirm} onChange={set("confirm")} invalid={!!errors.confirm} />
          </Field>
          {errors._form && (
            <p role="alert" className="rounded-xl bg-error-container px-3.5 py-3 text-body-md font-medium text-on-error-container">
              {errors._form}
            </p>
          )}
          <Button type="submit" size="lg" block loading={pending} disabled={!values.current || !values.next || !values.confirm}>
            Save and continue
          </Button>
          <Button type="button" variant="tonal" block leading={<LogOut />} onClick={logout}>
            Log out
          </Button>
        </form>
      </div>
    </div>
  );
}
